import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import Icon from 'components/icon/Icon';
import { getAPI, postAPI } from 'apis/backend/apiNode';

// -------- Styles identiques --------
const S = {
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: '1rem',
  },
  productItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 13,
  },
  label: {
    fontWeight: 600,
    color: '#0f172a',
  },
  input: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    color: '#1e293b',
    padding: '5px 10px',
    fontSize: 12,
    minWidth: '150px',
  },
  select: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    color: '#1e293b',
    padding: '5px 10px',
    fontSize: 12,
    minWidth: '160px',
  },
};

const StockTransferModal = ({ intervention, show, onHide, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [principalStock, setPrincipalStock] = useState([]);
  const [carStock, setCarStock] = useState([]);

  // -------- Filtres --------
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);

  // État pour la saisie de quantité
  const [showQtyPrompt, setShowQtyPrompt] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [qty, setQty] = useState(1);
  const [reserving, setReserving] = useState(false);

  // -------- Chargement des catégories --------
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getAPI('stock', '/categories');
        if (res.success) setCategories(res.data.categories || []);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    if (show) fetchCategories();
  }, [show]);

  // -------- Chargement des stocks --------
  useEffect(() => {
    if (show && intervention?._id) {
      fetchStock();
    }
  }, [show, intervention]);

  const fetchStock = async () => {
    setLoading(true);
    setError(null);
    try {
      const principalRes = await getAPI('stock', '/entrepots?code=PRINCIPAL');
      const principalEntrepot = principalRes?.data?.entrepots?.[0] || principalRes;
      if (!principalEntrepot) throw new Error('Aucun entrepôt principal configuré');

      const carId = intervention.cars[0]?._id;
      console.log("intervention****",intervention)

      const [principalStockRes, carStockRes] = await Promise.all([
        getAPI('stock', `/entrepots/${principalEntrepot._id}/inventaire/dispo`),
        getAPI('stock', `/entrepots/${carId}/inventaire/dispo`),
      ]);

      const buildItems = (stockData, source) => {
        const inventaire = stockData?.data?.inventaire || stockData || [];
        return inventaire.map(item => ({
          id: item.produitId?._id,
          name: item.produitId?.description || item.produitId?.name || 'Produit',
          unit: item.produitId?.unite || 'unité',
          available: item.quantite - item.quantiteReservee || 0,
          entrepotId: source === 'principal' ? principalEntrepot._id : carId,
          carId: source === 'car' ? carId : null,
          category: item.produitId?.category,
        }));
      };

      setPrincipalStock(buildItems(principalStockRes, 'principal'));
      setCarStock(carId ? buildItems(carStockRes, 'car') : []);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Impossible de charger les stocks.');
    } finally {
      setLoading(false);
    }
  };

  // -------- Filtrage côté client --------
  const filterProducts = (products) => {
    return products.filter(p => {
      // Recherche
      const matchSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
      // Catégorie
      let matchCategory = true;
      if (categoryFilter) {
        const cat = p.category;
        const hasCategory = (category, categoryId) => {
          if (!category) return false;
          if (category.current?._id === categoryId) return true;
          return category.parents?.some(c => c._id === categoryId) || false;
        };
        matchCategory = hasCategory(cat, categoryFilter);
      }
      return matchSearch && matchCategory;
    });
  };

  // -------- Réservation --------
  const openQtyPrompt = (product, source) => {
    setSelectedProduct(product);
    setSelectedSource(source);
    setQty(1);
    setShowQtyPrompt(true);
  };

  const handleReserve = async () => {
    if (!selectedProduct || qty < 1 || qty > selectedProduct.available) {
      alert('Quantité invalide.');
      return;
    }

    setReserving(true);
    try {
      const payload = {
        produitId: selectedProduct.id,
        quantite: qty,
        source: 'intervention',
        sourceId: intervention._id,
        notes: `intervention ${intervention?.reference}`,
        entrepotId: selectedSource === 'principal' ? selectedProduct.entrepotId : null,
        carId: selectedSource === 'car' ? selectedProduct.carId : null,
      };
      await postAPI('stock', payload, '/reserver');
      await fetchStock();
      if (onConfirm) onConfirm();
      setShowQtyPrompt(false);
      onHide();
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    } finally {
      setReserving(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
  };

  // -------- Rendu des listes filtrées --------
  const renderProductList = (products, sourceLabel) => {
    const filtered = filterProducts(products);
    if (filtered.length === 0) {
      return <p className="text-muted" style={{ fontSize: 13 }}>Aucun produit ne correspond aux filtres</p>;
    }
    return filtered.map(product => (
      <div key={product.id} style={S.productItem}>
        <div>
          <div style={S.label}>{product.name}</div>
          <small style={{  }}>
            Disponible : {product.available} 
          </small>
          <br/>
           <small style={{ color: '#94a3b8' }}>
            {product.category?.path || 'Sans catégorie'}
          </small>
        </div>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => openQtyPrompt(product, sourceLabel === 'Principal' ? 'principal' : 'car')}
          disabled={product.available <= 0}
        >
          Réserver
        </Button>
      </div>
    ));
  };

  // -------- Chargement / Erreur --------
  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Body className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Chargement des stocks...</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon icon="PlusCircle" className="me-2" />
            Réserver du stock
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Barre de filtres */}
          <div style={{ ...S.card, marginBottom: '1rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Rechercher un produit..."
                style={S.input}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <select
                style={S.select}
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
              >
                <option value="">Toutes catégories</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
              <Button variant="outline-secondary" size="sm" onClick={resetFilters}>
                Réinitialiser
              </Button>
            </div>
          </div>

          {/* Colonnes */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {/* Stock Principal */}
            <div style={{ flex: '1 1 45%', minWidth: '250px' }}>
              <div style={S.card}>
                <h6 style={{ fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                  <Icon icon="Warehouse" className="me-1" />
                  Entrepôt Principal
                  <Badge bg="secondary" className="ms-2">
                    {filterProducts(principalStock).reduce((sum, p) => sum + p.available, 0)}
                  </Badge>
                </h6>
                {renderProductList(principalStock, 'Principal')}
              </div>
            </div>

            {/* Stock Véhicule */}
            <div style={{ flex: '1 1 45%', minWidth: '250px' }}>
              <div style={S.card}>
                <h6 style={{ fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                  <Icon icon="Truck" className="me-1" />
                  Véhicule
                  <Badge bg="secondary" className="ms-2">
                    {filterProducts(carStock).reduce((sum, p) => sum + p.available, 0)}
                  </Badge>
                </h6>
                {renderProductList(carStock, 'Véhicule')}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Prompt de quantité */}
      <Modal show={showQtyPrompt} onHide={() => setShowQtyPrompt(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Quantité à réserver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: 12 }}>
            <strong>{selectedProduct?.name}</strong>
            <br />
            <small style={{ color: '#64748b' }}>
              Source : {selectedSource === 'principal' ? 'Principal' : 'Véhicule'}
              <br />
              Disponible : {selectedProduct?.available} {selectedProduct?.unit}
            </small>
          </div>
          <input
            type="number"
            className="form-control"
            min="1"
            max={selectedProduct?.available || 1}
            value={qty}
            onChange={(e) => setQty(parseInt(e.target.value) || 1)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQtyPrompt(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleReserve} disabled={reserving}>
            {reserving ? 'Réservation...' : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StockTransferModal;